/**
 * Hook useVersionCheck
 * 
 * Verifica periodicamente se há uma nova versão do app disponível
 * comparando o buildId atual com o version.json no servidor.
 * 
 * Features:
 * - Polling a cada 60-120 segundos quando app está ativo
 * - Verificação ao voltar para o app (visibility change)
 * - Detecção de Service Worker e Cache Storage
 * - Fluxo de atualização robusto
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { BUILD_ID, APP_VERSION, compareVersions } from '@/lib/buildInfo';

export interface VersionInfo {
  buildId: string;
  deployedAt: string;
  version: string;
  commitHash: string;
}

export interface UpdateDiagnostics {
  hasServiceWorker: boolean;
  registrations: number;
  cacheNames: string[];
  isPWA: boolean;
  currentBuildId: string;
  serverBuildId: string | null;
  lastCheck: string | null;
}

interface UseVersionCheckOptions {
  /** Intervalo de polling em ms (default: 90000 = 90s) */
  pollInterval?: number;
  /** Habilitar polling automático (default: true) */
  enablePolling?: boolean;
  /** Callback quando nova versão é detectada */
  onUpdateAvailable?: (serverVersion: VersionInfo) => void;
}

export function useVersionCheck(options: UseVersionCheckOptions = {}) {
  // Limpar parâmetro _v da URL após reload de cache-busting
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_v')) {
      url.searchParams.delete('_v');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  }, []);
  const {
    pollInterval = 90000,
    enablePolling = true,
    onUpdateAvailable,
  } = options;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState<VersionInfo | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateStep, setUpdateStep] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<UpdateDiagnostics | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const lastCheckRef = useRef<string | null>(null);
  const onUpdateAvailableRef = useRef(onUpdateAvailable);
  onUpdateAvailableRef.current = onUpdateAvailable;

  /**
   * Coletar diagnósticos do ambiente
   */
  const collectDiagnostics = useCallback(async (): Promise<UpdateDiagnostics> => {
    let hasServiceWorker = false;
    let registrations = 0;
    let cacheNames: string[] = [];
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  (window.navigator as any).standalone === true;

    if ('serviceWorker' in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        hasServiceWorker = regs.length > 0;
        registrations = regs.length;
      } catch (e) {
        console.warn('[VersionCheck] Erro ao verificar SW:', e);
      }
    }

    if ('caches' in window) {
      try {
        cacheNames = await caches.keys();
      } catch (e) {
        console.warn('[VersionCheck] Erro ao listar caches:', e);
      }
    }

    return {
      hasServiceWorker,
      registrations,
      cacheNames,
      isPWA,
      currentBuildId: BUILD_ID,
      serverBuildId: serverVersion?.buildId || null,
      lastCheck: lastCheckRef.current,
    };
  }, [serverVersion]);

  /**
   * Verificar se há nova versão
   */
  const checkForUpdate = useCallback(async (): Promise<boolean> => {
    if (isChecking) return false;
    
    setIsChecking(true);
    setError(null);

    try {
      // Buscar version.json com cache-busting
      const cacheBuster = `_cb=${Date.now()}`;
      const response = await fetch(`/version.json?${cacheBuster}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: VersionInfo = await response.json();
      lastCheckRef.current = new Date().toISOString();
      setServerVersion(data);

      // Comparar buildId E versão semântica (só mostra update se servidor for MAIOR)
      const hasUpdate = !import.meta.env.DEV && 
        data.buildId !== BUILD_ID &&
        compareVersions(data.version, APP_VERSION) > 0;
      
      if (hasUpdate && !updateAvailable) {
        console.log('[VersionCheck] Nova versão detectada:', {
          current: BUILD_ID,
          server: data.buildId,
        });
        setUpdateAvailable(true);
        onUpdateAvailableRef.current?.(data);
      }

      // Atualizar diagnósticos
      const diag = await collectDiagnostics();
      diag.serverBuildId = data.buildId;
      diag.lastCheck = lastCheckRef.current;
      setDiagnostics(diag);

      return hasUpdate;
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Erro desconhecido';
      console.warn('[VersionCheck] Falha ao verificar versão:', errorMsg);
      setError(errorMsg);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, [isChecking, updateAvailable, collectDiagnostics]);

  /**
   * Executar atualização completa
   */
  const performUpdate = useCallback(async () => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    setError(null);
    
    try {
      // Passo 1: Atualizar Service Worker
      if ('serviceWorker' in navigator) {
        setUpdateStep('Atualizando Service Worker...');
        console.log('[VersionCheck] Atualizando Service Worker...');
        
        const registrations = await navigator.serviceWorker.getRegistrations();
        
        for (const registration of registrations) {
          try {
            // Forçar verificação de atualização
            await registration.update();
            
            // Se há worker em espera, ativá-lo
            if (registration.waiting) {
              console.log('[VersionCheck] Ativando worker em espera...');
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
              
              // Aguardar controllerchange ou timeout
              await Promise.race([
                new Promise<void>((resolve) => {
                  navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
                }),
                new Promise<void>((resolve) => setTimeout(resolve, 3000)),
              ]);
            }
          } catch (e) {
            console.warn('[VersionCheck] Erro ao atualizar registration:', e);
          }
        }
      }

      // Passo 2: Limpar Cache Storage
      if ('caches' in window) {
        setUpdateStep('Limpando cache...');
        console.log('[VersionCheck] Limpando Cache Storage...');
        
        const cacheNames = await caches.keys();
        const appCaches = cacheNames.filter(name => 
          name.startsWith('workbox-') || 
          name.includes('standfull') ||
          name.includes('sw-') ||
          name.includes('vite-')
        );
        
        for (const cacheName of appCaches) {
          try {
            await caches.delete(cacheName);
            console.log('[VersionCheck] Cache removido:', cacheName);
          } catch (e) {
            console.warn('[VersionCheck] Erro ao remover cache:', cacheName, e);
          }
        }
      }

      // Passo 3: Reload com cache-busting
      setUpdateStep('Recarregando...');
      console.log('[VersionCheck] Recarregando página...');
      
      // Construir URL com cache-buster mantendo a rota atual
      const url = new URL(window.location.href);
      url.searchParams.set('_v', serverVersion?.buildId || Date.now().toString());
      
      // Usar replace para não poluir histórico
      window.location.replace(url.toString());
      
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Erro desconhecido';
      console.error('[VersionCheck] Erro na atualização:', errorMsg);
      setError(errorMsg);
      setIsUpdating(false);
      setUpdateStep(null);
      
      // Fallback: reload simples
      window.location.reload();
    }
  }, [isUpdating, serverVersion]);

  /**
   * Forçar atualização (mesmo sem nova versão detectada)
   */
  const forceUpdate = useCallback(async () => {
    await checkForUpdate();
    await performUpdate();
  }, [checkForUpdate, performUpdate]);

  // Polling periódico
  useEffect(() => {
    if (!enablePolling) return;

    // Verificar imediatamente ao montar
    const timeoutId = setTimeout(() => {
      checkForUpdate();
    }, 5000); // Delay inicial de 5s para não competir com hydration

    // Polling regular
    const intervalId = setInterval(() => {
      // Só verificar se a aba está visível
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    }, pollInterval);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [enablePolling, pollInterval, checkForUpdate]);

  // Verificar ao voltar para a aba
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Verificar se passou tempo suficiente desde última verificação
        const lastCheck = lastCheckRef.current;
        if (lastCheck) {
          const elapsed = Date.now() - new Date(lastCheck).getTime();
          if (elapsed > 30000) { // Pelo menos 30s desde última verificação
            checkForUpdate();
          }
        } else {
          checkForUpdate();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkForUpdate]);

  return {
    // Estado
    updateAvailable,
    serverVersion,
    isChecking,
    isUpdating,
    updateStep,
    error,
    diagnostics,
    currentBuildId: BUILD_ID,
    
    // Ações
    checkForUpdate,
    performUpdate,
    forceUpdate,
    collectDiagnostics,
  };
}
